/**
 * Active Medications Hook
 *
 * Fetches currently active medications from the events API.
 * Used to pre-populate the check-in form medication section
 * with medications the user is currently taking.
 *
 * Supports an optional `date` parameter (YYYY-MM-DD) so callers like
 * the medication-switch event form can fetch active medications for a
 * specific date rather than today.
 */

import { useQuery } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type { ActiveMedication } from "@floline/api-client";
import { API_URL } from "@/env";

const EMPTY_ACTIVE_MEDICATIONS: ActiveMedication[] = [];

/** Query key factory for active medications */
export const activeMedicationKeys = {
  all: ["active-medications"] as const,
  /** Query key scoped to a specific date (YYYY-MM-DD) */
  byDate: (date: string | undefined) =>
    date
      ? (["active-medications", date] as const)
      : (["active-medications"] as const),
};

/** API client configured for requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Hook to fetch currently active medications from events
 *
 * Returns active medication events (medication_start with no endDate
 * or endDate >= the requested date). Used to pre-populate the check-in
 * form and the medication-switch event form.
 *
 * @param options.enabled - Whether to fetch (defaults to true)
 * @param options.date    - Optional date (YYYY-MM-DD) to query active
 *                          medications for. Defaults to today on the server.
 *
 * @example
 * ```tsx
 * // Fetch active meds for today (default)
 * const { activeMedications, isLoading } = useActiveMedications();
 *
 * // Fetch active meds for a specific date
 * const { activeMedications } = useActiveMedications({ date: "2026-01-15" });
 * ```
 */
export function useActiveMedications(options?: {
  enabled?: boolean;
  date?: string;
}) {
  const date = options?.date;

  const query = useQuery({
    queryKey: activeMedicationKeys.byDate(date),
    queryFn: async () => {
      const response = await apiClient.getActiveMedications(
        date ? { date } : undefined
      );
      return response.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes - medications change infrequently
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && (error.status === 401 || error.status === 404)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  return {
    /** Array of currently active medications */
    activeMedications: query.data ?? EMPTY_ACTIVE_MEDICATIONS,
    /** Whether data is currently loading */
    isLoading: query.isLoading,
    /** Whether the fetch failed */
    isError: query.isError,
    /** Error details */
    error: query.error,
  };
}
